import React, { useEffect, useState } from 'react';
import { useNewConsentModalContext } from 'pages/SharePage/Context/NewConsentModalContext';
import { BrInput, BrButton } from '@govbr-ds/react-components';
import { Box } from '@material-ui/core';
import '../ReprocessingPage.css';
import { connectorRequests } from 'apiServices/connectorMonitorAPI/connectorMonitor';
import { GovBrDateInput, GovBrSelect } from 'components';
import MHDRequests from 'services/MHDRequests';
import { oids } from 'configs';
import { toast } from 'react-toastify';

function cleanConnectorResponse(data) {
  const dictTypeDocuments = {
    PatientData: 'Cadastro de paciente',
    RACData: 'Registro de atendimento clínico',
    SAData: 'Sumário de alta',
    AttachmentModel: 'Documento assinado (atestados, prescrições, etc.)',
    ResultadoExameData: 'Resultado de exame',
  };

  const responseItem = data
    ?.map((el) => {
      const populateActions = el?.action
        ? el.action?.map((act) => {
            const value = {
              patientId: act?.parameters?.patient || act?.parameters?.patientId,
              documentId:
                act?.parameters?.identifier || act?.parameters?.localId,
              documentType: dictTypeDocuments[act?.parameters?.model] || '-',
              organization: el.organization,
              status: el.status,
              startDate: el?.created || '-',
              endDate: el?.updated || '-',
            };
            return value;
          })
        : {
            patientId: '-',
            documentId: '-',
            documentType: '-',
            organization: el.organization,
            status: el.status,
            startDate: el?.created || '-',
            endDate: el?.updated || '-',
          };
      return populateActions;
    })
    ?.filter((e) => e !== undefined);

  const lines = [].concat([], ...responseItem);
  return lines;
}

// eslint-disable-next-line react/prop-types
export function SearchFields({ setSearch, setRows, loading, setLoading }) {
  const { organizationList } = useNewConsentModalContext();

  const [identifier, setIdentifier] = useState('');
  const [healthLocale, setHealthLocale] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [render, setRender] = useState(true);

  const [dataStart, setDataStart] = useState('');
  const [dataEnd, setDataEnd] = useState('');

  const arrayTypeDocuments = [
    'Cadastro de paciente',
    'Documento assinado (atestados, prescrições, etc.)',
    'Registro de atendimento clínico',
    'Resultado de exame',
    'Sumário de alta',
  ];

  const dictTypeDocuments = {
    'Cadastro de paciente': 'PatientData',
    'Registro de atendimento clínico': 'RACData',
    'Sumário de alta': 'SAData',
    'Documento assinado (atestados, prescrições, etc.)': 'AttachmentModel',
    'Resultado de exame': 'ResultadoExameData',
  };

  const handleSearch = async () => {
    let cpf;

    if (identifier?.length == 11 || identifier?.length === 15) {
      const patientJson = await MHDRequests.getPatientJson({
        patientId: identifier,
      });

      const patientIdentifier = patientJson?.identifier?.find((element) =>
        element.system.includes(oids.cpf),
      )?.value;

      cpf = patientIdentifier;
    }
    const cnesToSearch = organizationList?.find(
      (el) => el?.resource?.alias?.[0] === healthLocale,
    )?.resource?.identifier?.[0]?.value;
    const documentToSearch = dictTypeDocuments[`${documentType}`];

    const auxStartDate = dataStart.split('/')?.reverse().join('-');
    const auxEndDate = dataEnd.split('/')?.reverse().join('-');

    const auxParam = [
      { param: cpf && `"patient":"${cpf}"` },
      { param: documentToSearch && `"model":"${documentToSearch}"` },
    ]
      ?.filter((el) => el.param)
      ?.map((e) => e.param)
      ?.join(',');

    const connectorFilters = [
      {
        filter: cnesToSearch && `organization=${cnesToSearch}`,
      },
      {
        filter: dataStart && `start=${auxStartDate}`,
      },
      {
        filter: dataEnd && `end=${auxEndDate}`,
      },
      {
        filter: auxParam && `parameters={${auxParam}}`,
      },
    ]
      ?.filter((el) => el.filter)
      ?.map((e) => e.filter)
      ?.join('&');

    try {
      if (identifier && identifier?.length !== 11 && identifier?.length !== 15)
        throw new Error('Identifier');
      if (identifier?.length && !cpf) throw new Error('Not found');
      if (dataStart && dataStart?.length !== 10) throw new Error('Start');
      if (dataEnd && dataEnd?.length !== 10) throw new Error('End');

      setSearch(true);

      const connector_log = await connectorRequests.getTasks(connectorFilters);
      setRows(cleanConnectorResponse(connector_log?.['_items'] || []));

      toast.success(`A busca foi realizada com sucesso`);
    } catch (err) {
      console.log('err', err);
      if (err.toString().includes('Limit')) {
        toast.warn(
          'Grande volume de ocorrências. Utilize os filtros para restringir os resultados',
        );
        return;
      }
      if (err.toString().includes('Start')) {
        toast.warn('Data inicial inválida');
      }
      if (err.toString().includes('End')) {
        toast.warn('Data final inválida');
      }
      if (err.toString().includes('Identifier')) {
        toast.warn('CPF ou CNS inválido');
      }
      if (err.toString().includes('Not found')) {
        toast.warn('Paciente não encontrado');
      }
      toast.error(
        'Não foi possível finalizar a operação, verifique os filtros e tente novamente',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setIdentifier('');
    setHealthLocale('');
    setDocumentType('');
    setDataStart('');
    setDataEnd('');
    setSearch(false);
    setRender(false);
    setRows([]);
  };

  useEffect(() => {
    if (!render) setRender(true);
  }, [render]);

  useEffect(() => {
    async function init() {
      await handleSearch();
    }
    if (!loading) init();
  }, []);

  return (
    <>
      {render && (
        <>
          <div className="row">
            <div className="col mb-1 selectMobile">
              <BrInput
                style={{
                  border: '1px solid',
                  borderColor: 'var(--secondary-background-color)',
                }}
                value={identifier}
                placeholder="CPF ou CNS do paciente"
                label="CPF ou CNS"
                type="datetime"
                onChange={(e) => setIdentifier(e.target.value)}
              />
            </div>
            <div className="col mb-1 selectMobile">
              <GovBrSelect
                value={healthLocale}
                setValue={setHealthLocale}
                list={organizationList?.map((el) => el?.resource?.alias?.[0])}
                label="Unidade hospitalar"
              />
            </div>

            <div className="col mb-1 selectMobile">
              <GovBrSelect
                value={documentType}
                setValue={setDocumentType}
                list={arrayTypeDocuments}
                label="Tipos de documentos"
              />
            </div>
            <GovBrDateInput
              value={dataStart}
              setValue={setDataStart}
              label="Data inicial"
            />
            <GovBrDateInput
              value={dataEnd}
              setValue={setDataEnd}
              label="Data final"
            />
          </div>

          <Box display="flex" flexDirection="row-reverse" gridGap={20}>
            <BrButton primary sx={{ width: '10%' }} onClick={handleSearch}>
              Pesquisar
            </BrButton>
            <BrButton secondary sx={{ width: '10%' }} onClick={handleClear}>
              Limpar
            </BrButton>
          </Box>
        </>
      )}
    </>
  );
}
